import styles from './M32ProductTabs.module.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { ProductTabsItem, ProductTabsItemProps } from './ProductTabsItem';
import { ProductTabsContent, ProductTabsContentProps } from './ProductTabsContent';
import { Container } from 'ui/components/4-habitats';
import { Heading } from 'ui/components/1-atoms';

export interface M32ProductTabsProps {
	heading?: string;
	productTabs: ProductTabsItemProps[];
	productContent: ProductTabsContentProps[];
	activeId?: string;
	onActiveChange?: (id: string) => void;
	defaultActiveId?: string;
	anchor?: string;
}

export const M32ProductTabs: React.FC<M32ProductTabsProps> = ({
	heading,
	productTabs,
	productContent,
	activeId,
	onActiveChange,
	defaultActiveId,
	anchor,
}) => {
	// Using index instead of tab headings as ID
	const indexedTabs = useMemo(
		() =>
			productTabs.map((tab, index) => ({
				...tab,
				id: String(index),
			})),
		[productTabs],
	);

	const indexedContent = useMemo(
		() =>
			productContent.map((panel, index) => ({
				...panel,
				id: String(index),
			})),
		[productContent],
	);

	const ids = useMemo(() => indexedTabs.map((t) => t.id), [indexedTabs]);

	const [uncontrolledId, setUncontrolledId] = useState<string>(
		String(activeId ?? defaultActiveId ?? indexedTabs[0]?.id ?? indexedContent[0]?.id ?? '0'),
	);

	const currentActiveId = String(activeId ?? uncontrolledId);

	useEffect(() => {
		if (activeId != null) return;
		if (!ids.length) {
			if (uncontrolledId) setUncontrolledId('');
			return;
		}
		if (!currentActiveId || !ids.includes(currentActiveId)) {
			setUncontrolledId(ids[0]);
		}
	}, [activeId, currentActiveId, ids, uncontrolledId]);

	const setActive = (id: string) => {
		if (activeId != null) {
			onActiveChange?.(id);
		} else {
			setUncontrolledId(id);
			onActiveChange?.(id);
		}
	};

	const activePanel = useMemo(() => indexedContent.find((p) => p.id === currentActiveId), [indexedContent, currentActiveId]);

	return (
		<section className={styles.M32ProductTabs_wrapper} id={anchor}>
			<div className={styles.M32ProductTabs}>
				<Container width="StandardWeb">
					{heading && (
						<Heading className={styles.M32ProductTabs_tabHeading} headingLevel="h2" style="headlineLg">
							{heading}
						</Heading>
					)}

					<div className={styles.M32ProductTabs_tabContainer} role="tablist" aria-label={heading || 'Product tabs'}>
						{indexedTabs.map((tab) => (
							<ProductTabsItem key={tab.id} {...tab} isActive={currentActiveId} setIsActive={setActive} />
						))}
					</div>
				</Container>
			</div>

			<Container width="StandardWeb">
				<div className={styles.M32ProductTabs_contentContainer}>
					{activePanel && <ProductTabsContent key={activePanel.id} {...activePanel} isActive={currentActiveId} />}
				</div>
			</Container>
		</section>
	);
};
